import React, { useState, useRef, useEffect, useMemo } from 'react'
import Icon from '../icon'
import { InputLabel, Slider, Typography } from '@mui/material'
import CropIcon from '@mui/icons-material/Crop'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import styles from './styles.module.css'
import { trimStringToLength } from '../../util/util'
import { fetchMedia } from '../../api-client/data'

const CropperVideoPlayer = ({ onCropAreaSelected = () => { }, flatValues }) => {
    const [options, setOptions] = useState()

    useEffect(() => {
        async function fetchData() {
            const ret = await Promise.all(
                flatValues
                    ?.filter(x => typeof x === "string" ? x.includes("_viewable") : false)
                    ?.map(async x => {
                        const media = await fetchMedia(x.replace("media:", ""))
                        return {
                            ...media,
                            id: x,
                        }
                    })
            )
            setOptions(ret)
        }
        fetchData()
    }, [flatValues])

    const [src, setSrc] = useState(null)
    const [isVideo, setIsVideo] = useState(false)

    const mediaRef = useRef(null)
    const containerRef = useRef(null)
    const canvasRef = useRef(null)

    const [isPaused, setIsPaused] = useState(true)
    const [mediaDuration, setMediaDuration] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)

    const [cropMode, setCropMode] = useState(false)
    const [isCropping, setIsCropping] = useState(false)

    const [startX, setStartX] = useState(0)
    const [startY, setStartY] = useState(0)
    const [cropWidth, setCropWidth] = useState(0)
    const [cropHeight, setCropHeight] = useState(0)

    const [selectedRect, setSelectedRect] = useState(null)

    const [rectsByFile, setRectsByFile] = useState({})

    const [originalMediaWidth, setOriginalMediaWidth] = useState(0)
    const [originalMediaHeight, setOriginalMediaHeight] = useState(0)

    useEffect(() => {
        return () => {
            if (src) {
                URL.revokeObjectURL(src)
            }
        }
    }, [src])

    const handleFileUpload = (event) => {
        const filePath = event.target.value

        setSrc(filePath)
        setIsVideo(filePath.toLowerCase().endsWith('.mp4'))

        if (rectsByFile[filePath]) {
            setSelectedRect(rectsByFile[filePath])
        } else {
            setSelectedRect(null)
        }
    }

    const resizeCanvas = () => {
        if (!mediaRef.current || !canvasRef.current) return
        const media = mediaRef.current
        const canvas = canvasRef.current

        // Match the canvas to the client's displayed width/height
        canvas.width = media.clientWidth
        canvas.height = media.clientHeight
        canvas.style.width = `${media.clientWidth}px`
        canvas.style.height = `${media.clientHeight}px`
        canvas.style.left = `${media.offsetLeft}px`
        canvas.style.top = `${media.offsetTop}px`

        drawCropArea()
    }

    useEffect(() => {
        window.addEventListener('resize', resizeCanvas)
        return () => window.removeEventListener('resize', resizeCanvas)
    }, [])

    useEffect(() => {
        drawCropArea()
    }, [selectedRect, cropMode])

    useEffect(() => {
        if (cropMode && isVideo && mediaRef.current && !mediaRef.current.paused) {
            mediaRef.current.pause()
            setIsPaused(true)
        }
    }, [cropMode, isVideo])

    const handlePlayPauseToggle = () => {
        if (isVideo && mediaRef.current) {
            if (mediaRef.current.paused) {
                mediaRef.current.play()
                setIsPaused(false)
            } else {
                mediaRef.current.pause()
                setIsPaused(true)
            }
        }
    }

    const handleTimeChange = (event, newValue) => {
        if (isVideo && mediaRef.current) {
            mediaRef.current.currentTime = newValue
            setCurrentTime(newValue)
        }
    }

    const handleLoadedMetadata = (e) => {
        if (isVideo) {
            setMediaDuration(e.target.duration)
            setCurrentTime(e.target.currentTime)
            setOriginalMediaWidth(e.target.videoWidth)
            setOriginalMediaHeight(e.target.videoHeight)
        }
        resizeCanvas()
    }

    const handleImageLoad = (e) => {
        setOriginalMediaWidth(e.target.naturalWidth)
        setOriginalMediaHeight(e.target.naturalHeight)
        resizeCanvas()
    }

    const handleTimeUpdate = (e) => {
        if (isVideo) {
            setCurrentTime(e.target.currentTime)
        }
    }

    const handleStart = (clientX, clientY) => {
        if (!cropMode) return
        setIsCropping(true)
    
        const rect = canvasRef.current.getBoundingClientRect()
        const scaleX = canvasRef.current.width / rect.width
        const scaleY = canvasRef.current.height / rect.height
    
        setStartX((clientX - rect.left) * scaleX)
        setStartY((clientY - rect.top) * scaleY)
    }
    
    const handleMove = (clientX, clientY) => {
        if (!cropMode || !isCropping) return
    
        const rect = canvasRef.current.getBoundingClientRect()
        const scaleX = canvasRef.current.width / rect.width
        const scaleY = canvasRef.current.height / rect.height
    
        const currentX = (clientX - rect.left) * scaleX
        const currentY = (clientY - rect.top) * scaleY
    
        setCropWidth(currentX - startX)
        setCropHeight(currentY - startY)
    
        drawCropArea({
            previewRect: {
                x: startX,
                y: startY,
                width: currentX - startX,
                height: currentY - startY,
            },
        })
    }
    
    const handleEnd = () => {
        if (!cropMode || !isCropping) return
        setIsCropping(false)
    
        const displayWidth = mediaRef.current.clientWidth
        const displayHeight = mediaRef.current.clientHeight
    
        const ratioX = originalMediaWidth / displayWidth
        const ratioY = originalMediaHeight / displayHeight
    
        const originalX = startX * ratioX
        const originalY = startY * ratioY
        const originalW = cropWidth * ratioX
        const originalH = cropHeight * ratioY
    
        onCropAreaSelected({
            x1: originalX,
            y1: originalY,
            x2: originalX + originalW,
            y2: originalY + originalH,
            width: originalW,
            height: originalH,
        })
    
        setSelectedRect({
            x: originalX,
            y: originalY,
            width: originalW,
            height: originalH,
        })
    
        setRectsByFile((prev) => ({
            ...prev,
            [src]: { x: originalX, y: originalY, width: originalW, height: originalH },
        }))
    
        setCropMode(false)
    }
    
    const handleMouseDown = (e) => handleStart(e.clientX, e.clientY)
    const handleMouseMove = (e) => handleMove(e.clientX, e.clientY)
    const handleMouseUp = handleEnd
    
    const handleTouchStart = (e) => handleStart(e.touches[0].clientX, e.touches[0].clientY)
    const handleTouchMove = (e) => handleMove(e.touches[0].clientX, e.touches[0].clientY)
    const handleTouchEnd = handleEnd

    const drawCropArea = ({ previewRect } = {}) => {
        if (!canvasRef.current) return
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        // Draw previously selected crop
        if (selectedRect) {
            const displayWidth = canvas.width
            const displayHeight = canvas.height

            const scaleX = displayWidth / originalMediaWidth
            const scaleY = displayHeight / originalMediaHeight

            const x = selectedRect.x * scaleX
            const y = selectedRect.y * scaleY
            const w = selectedRect.width * scaleX
            const h = selectedRect.height * scaleY

            ctx.strokeStyle = 'white'
            ctx.lineWidth = 2
            ctx.strokeRect(x, y, w, h)
            ctx.fillStyle = 'rgba(0, 68, 255, 0.3)'
            ctx.fillRect(x, y, w, h)
        }

        // Draw the preview rectangle while cropping
        if (previewRect) {
            ctx.strokeStyle = 'white'
            ctx.lineWidth = 2
            ctx.strokeRect(
                previewRect.x,
                previewRect.y,
                previewRect.width,
                previewRect.height
            )
            ctx.fillStyle = 'rgba(253, 253, 253, 0.14)'
            ctx.fillRect(
                previewRect.x,
                previewRect.y,
                previewRect.width,
                previewRect.height
            )
        }
    }

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60)
        const seconds = Math.floor(time % 60)
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }

    const handleChangeSrc = () => {
        setSrc(null)
        setSelectedRect(null)
    }

    return (
        <div>
            {src ? (
                <div className={styles.videoContainer} ref={containerRef}>
                        {isVideo ? (
                            <video
                                ref={mediaRef}
                                src={src}
                                className={styles.videoPlayer}
                                onLoadedMetadata={handleLoadedMetadata}
                                onTimeUpdate={handleTimeUpdate}
                                muted
                            />
                        ) : (
                            <img
                                ref={mediaRef}
                                src={src}
                                className={styles.videoPlayer}
                                onLoad={handleImageLoad}
                                alt="uploaded"
                            />
                        )}

                        <canvas
                            ref={canvasRef}
                            className={styles.canvasOverlay}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            style={{
                                cursor: cropMode ? 'crosshair' : 'default',
                                pointerEvents: cropMode ? 'auto' : 'none',
                                zIndex: 2000,
                            }}
                        />

                    {!cropMode && (
                        <div className={styles.bottomControls} style={{ zIndex: 1 }}>
                            <div className={styles.controlsContainer}>
                                {isVideo && (
                                    <button
                                        className={styles.controlButton}
                                        onClick={handlePlayPauseToggle}
                                    >
                                        {isPaused ? (
                                            <Icon type="play" className={styles.playButton} />
                                        ) : (
                                            <Icon type="pause" className={styles.playButton} />
                                        )}
                                    </button>
                                )}

                                {isVideo && (
                                    <Typography variant="body2" className={styles.timer}>
                                        {formatTime(currentTime)} / {formatTime(mediaDuration)}
                                    </Typography>
                                )}

                                <button
                                    className={styles.controlButton}
                                    onClick={() => setCropMode(true)}
                                >
                                    <CropIcon />
                                </button>

                                <button className={styles.controlButton} onClick={handleChangeSrc}>
                                    <SwapHorizIcon />
                                </button>
                            </div>

                            {isVideo && (
                                <Slider
                                    min={0}
                                    max={mediaDuration}
                                    value={currentTime}
                                    onChange={handleTimeChange}
                                    className={styles.seekSlider}
                                    style={{ color: 'var(--text-primary)' }}
                                />
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div
                    style={{
                        paddingTop: '20px',
                        paddingBottom: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <InputLabel style={{color: 'var(--text-primary)'}} id="select-label">Select a cropping template</InputLabel>
                    <select
                        style={{ boxSizing: 'border-box', width: '100%', maxWidth: 350, color: 'var(--text-primary)', backgroundColor: 'var(--background-secondary)', padding: '0.5rem', borderRadius: '0.5rem', border: 'none', marginBottom: '1rem' }}
                        onChange={handleFileUpload}
                    >
                        <option value="" disabled selected>Select your option</option>
                        <option value="https://nuraml-irispublic.s3.amazonaws.com/Screenshot from 2025-01-11 17-36-54.png">Desktop</option>
                        <option value="https://nuraml-irispublic.s3.amazonaws.com/Screenshot from 2025-01-11 17-36-29.png">Mobile</option>
                        {options?.map((file, idx) => (
                            <option key={idx} value={file?.bucketPath}>
                                {trimStringToLength(file?.fileName, 5)}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    )
}

export default CropperVideoPlayer
